import classnames from 'classnames';
import React, { memo, useEffect, useCallback, useMemo, useRef, useState, } from 'react';
import { useDebounce } from 'react-use';
import FormControlErrorMessage from '../../utils/FormControlErrorMessage';
import FormControlInfoMessage from '../../utils/FormControlInfoMessage';
import Icon from '../../utils/Icon';
import useT from '../../utils/Translations/useT';
import useLayoutDirection from '../../utils/Translations/useLayoutDirection';
import useHelpNoteTitle from '../../utils/Translations/useHelpNoteTitle';
import AnimatedTitle from './AnimatedTitle';
import styles from './TextArea.scss';
import { isRTL } from '../../../utils/commonHelper';
import { getOracleByteLength } from '../../../utils/oracleStringHelper';
import { getInputDirection } from './withInputDirection';
const DEFAULT_ROWS = 2;
const TIMEOUT = 200;
const TextArea = ({ onChange, onPaste, onBlur, rows = DEFAULT_ROWS, name, value = '', leftIcon = null, errorMessage = null, label, placeholder, inputProps = {}, disabled = false, animateTitle = false, formElement = true, required = false, className = '', wrapperClassName = '', infoMessage = null, infoMessageAlign = 'left', onKeyDown, handleSelect, onMouseUp, autoHeight = true, maxLength, minLength = 0, isControl = true, hasCharactersCounter = true, infoMessageClassName = '', getOracleLength = false, dataCy, tooltipTitle, }) => {
    const t = useT();
    const { direction } = useLayoutDirection();
    const resolvedTooltipTitle = useHelpNoteTitle(label || placeholder, tooltipTitle);
    const textareaRef = useRef(null);
    const resolvedInputProps = inputProps;
    const [isFocus, setIsFocus] = useState(false);
    const wrapperClasses = useMemo(() => classnames({
        'form-group pb-10': formElement,
        'has-feedback': !!leftIcon,
        'has-feedback-left': !!leftIcon,
        'form-group-material': animateTitle,
    }, wrapperClassName), [animateTitle, formElement, leftIcon, wrapperClassName]);
    const _animateTitle = useMemo(() => animateTitle ? (<AnimatedTitle isRequired={required} placeholder={label || placeholder} tooltipTitle={tooltipTitle} value={value}/>) : null, [animateTitle, placeholder, label, required, tooltipTitle, value]);
    const _errorMessage = useMemo(() => errorMessage ? (<FormControlErrorMessage errorMessage={errorMessage}/>) : null, [errorMessage]);
    const _infoMessage = useMemo(() => {
        if (errorMessage) {
            return null;
        }
        if (infoMessage) {
            return (<FormControlInfoMessage additionalClassNames={infoMessageClassName} message={infoMessage} messageAlign={infoMessageAlign}/>);
        }
        if (maxLength && hasCharactersCounter && isFocus) {
            const textLength = getOracleLength
                ? getOracleByteLength(`${value}`)
                : `${value}`.length;
            return (<FormControlInfoMessage message={t('#{length}/#{maxLength}', {
                length: value ? textLength : 0,
                maxLength,
            })} messageAlign="right_end"/>);
        }
        return null;
    }, [
        value,
        t,
        maxLength,
        errorMessage,
        infoMessage,
        infoMessageAlign,
        hasCharactersCounter,
        infoMessageClassName,
        isFocus,
        getOracleLength,
    ]);
    const _leftIcon = useMemo(() => leftIcon ? (<div className="form-control-feedback">
          <Icon className="text-muted" name={leftIcon}/>
        </div>) : null, [leftIcon]);
    const setHeight = useCallback(() => {
        if (autoHeight && textareaRef.current) {
            const nextHeight = textareaRef.current.scrollHeight +
                (textareaRef.current.offsetHeight - textareaRef.current.clientHeight);
            if (!nextHeight) {
                return;
            }
            const prevHeight = Number(textareaRef.current.style.height.replace('px', ''));
            if (prevHeight > nextHeight) {
                return;
            }
            textareaRef.current.style.overflow = 'hidden';
            textareaRef.current.style.height = `${nextHeight}px`;
        }
    }, [autoHeight, textareaRef]);
    const delayedResize = useCallback(() => {
        if (autoHeight) {
            setTimeout(setHeight, 0);
        }
    }, [autoHeight, setHeight]);
    useEffect(() => {
        if (!autoHeight) {
            return undefined;
        }
        const animationFrame = window.requestAnimationFrame(setHeight);
        const timeout = window.setTimeout(setHeight, TIMEOUT);
        return () => {
            window.cancelAnimationFrame(animationFrame);
            window.clearTimeout(timeout);
        };
    }, [autoHeight, rows, setHeight, value]);
    useDebounce(() => {
        value && delayedResize();
    }, TIMEOUT, [delayedResize, value]);
    const handleChange = useCallback(e => {
        if (maxLength) {
            const newValue = e.target.value;
            const byteLength = getOracleLength
                ? getOracleByteLength(newValue)
                : newValue.length;
            if (byteLength > maxLength) {
                let truncatedValue = newValue;
                while (getOracleLength
                    ? getOracleByteLength(truncatedValue)
                    : truncatedValue.length > maxLength && truncatedValue.length > 0) {
                    truncatedValue = truncatedValue.slice(0, -1);
                }
                e.target.value = truncatedValue;
            }
        }
        delayedResize();
        onChange && onChange(e);
    }, [onChange, delayedResize, maxLength, getOracleLength]);
    const handlePaste = event => {
        onPaste && onPaste(event, name);
    };
    const handleBlur = useCallback(e => {
        onBlur && onBlur(e);
    }, []);
    const _onMouseUp = useCallback(e => {
        onMouseUp && onMouseUp(e);
    }, []);
    const handleDivBlur = useCallback(e => {
        setIsFocus(false);
        onBlur && onBlur(e);
    }, [onBlur]);
    const handleFocus = useCallback(e => {
        setIsFocus(true);
    }, []);
    const rtlClass = useMemo(() => (isRTL(value) ? styles.rtl : ``), [value]);
    const inputDirection = useMemo(() => getInputDirection(value, placeholder), [
        placeholder,
        value,
    ]);
    const _handleSelect = useCallback(event => {
        handleSelect &&
            handleSelect(event.target.value.substring(event.target.selectionStart, event.target.selectionEnd));
    }, [handleSelect]);
    return (<div className={wrapperClasses} data-layout-direction={direction} dir={direction} onBlur={handleDivBlur}>
      {_animateTitle}
      <textarea ref={textareaRef} className={classnames({ 'form-control': isControl }, styles.TextAreaField, rtlClass, className)} data-cy={dataCy} disabled={disabled} dir={resolvedInputProps.dir || inputDirection} minLength={minLength} name={name} placeholder={required ? `${placeholder} *` : placeholder} rows={rows} title={resolvedTooltipTitle} value={value} wrap="hard" onChange={handleChange} onPaste={handlePaste} onBlur={handleBlur} onFocus={handleFocus} onKeyDown={onKeyDown} onMouseUp={_onMouseUp} onSelect={_handleSelect} {...inputProps}/>
      {_errorMessage}
      {_infoMessage}
      {_leftIcon}
    </div>);
};
export default memo(TextArea);
